/**
 * Service de sauvegarde et restauration des données
 * Permet d'exporter et d'importer les données de l'application
 */

import { LocalStorageService } from './localStorageService';
import { toast } from 'react-hot-toast';

// Clés à inclure dans la sauvegarde
const BACKUP_KEYS = Object.values(LocalStorageService.STORAGE_KEYS);

/**
 * Crée une sauvegarde complète des données de l'application
 * @returns {Object} Objet contenant toutes les données sauvegardées
 */
const createBackup = () => {
  try {
    const backupData = {};
    const timestamp = new Date().toISOString();
    
    // Récupérer toutes les données du localStorage pour les clés définies
    BACKUP_KEYS.forEach(key => {
      const data = localStorage.getItem(key);
      if (data) {
        backupData[key] = data;
      }
    });
    
    // Ajouter des métadonnées à la sauvegarde
    const backup = {
      metadata: {
        timestamp,
        version: '1.0',
        appName: 'INVENTAIRE',
        keys: Object.keys(backupData)
      },
      data: backupData
    };
    
    return backup;
  } catch (error) {
    console.error('Erreur lors de la création de la sauvegarde:', error);
    throw new Error('Impossible de créer la sauvegarde');
  }
};

/**
 * Exporte la sauvegarde sous forme de fichier JSON téléchargeable
 */
const exportBackup = () => {
  try {
    const backup = createBackup();
    const backupString = JSON.stringify(backup, null, 2);
    const blob = new Blob([backupString], { type: 'application/json' });
    
    // Créer un lien de téléchargement
    const date = new Date().toISOString().split('T')[0];
    const filename = `inventaire_backup_${date}.json`;
    
    const downloadLink = document.createElement('a');
    downloadLink.href = URL.createObjectURL(blob);
    downloadLink.download = filename;
    
    // Déclencher le téléchargement
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    
    toast.success('Sauvegarde exportée avec succès');
    return true;
  } catch (error) {
    console.error('Erreur lors de l\'exportation de la sauvegarde:', error);
    toast.error('Erreur lors de l\'exportation de la sauvegarde');
    return false;
  }
};

/**
 * Restaure les données à partir d'un fichier de sauvegarde
 * @param {File} file - Fichier de sauvegarde à restaurer
 * @returns {Promise<boolean>} True si la restauration a réussi, false sinon
 */
const importBackup = (file) => {
  return new Promise((resolve, reject) => {
    try {
      if (!file) {
        toast.error('Aucun fichier sélectionné');
        reject(new Error('Aucun fichier sélectionné'));
        return;
      }
      
      const reader = new FileReader();
      
      reader.onload = (event) => {
        try {
          const backupContent = event.target.result;
          const backup = JSON.parse(backupContent);
          
          // Vérifier la structure du fichier de sauvegarde
          if (!backup.metadata || !backup.data) {
            toast.error('Format de fichier de sauvegarde invalide');
            reject(new Error('Format de fichier de sauvegarde invalide'));
            return;
          }
          
          // Afficher une confirmation avant de restaurer
          if (window.confirm(
            `Êtes-vous sûr de vouloir restaurer cette sauvegarde du ${new Date(backup.metadata.timestamp).toLocaleString()}? 
            Cela remplacera toutes vos données actuelles.`
          )) {
            // Restaurer les données
            const { data } = backup;
            
            // Restaurer chaque clé
            Object.keys(data).forEach(key => {
              localStorage.setItem(key, data[key]);
            });
            
            toast.success('Données restaurées avec succès');
            resolve(true);
          } else {
            toast.info('Restauration annulée');
            resolve(false);
          }
        } catch (error) {
          console.error('Erreur lors de la lecture du fichier de sauvegarde:', error);
          toast.error('Erreur lors de la lecture du fichier de sauvegarde');
          reject(error);
        }
      };
      
      reader.onerror = (error) => {
        console.error('Erreur lors de la lecture du fichier:', error);
        toast.error('Erreur lors de la lecture du fichier');
        reject(error);
      };
      
      reader.readAsText(file);
    } catch (error) {
      console.error('Erreur lors de l\'importation de la sauvegarde:', error);
      toast.error('Erreur lors de l\'importation de la sauvegarde');
      reject(error);
    }
  });
};

/**
 * Crée une sauvegarde automatique périodique
 * @param {number} intervalMinutes - Intervalle en minutes entre les sauvegardes
 * @returns {number} ID de l'intervalle pour pouvoir l'arrêter plus tard
 */
const startAutoBackup = (intervalMinutes = 30) => {
  // Convertir en millisecondes
  const interval = intervalMinutes * 60 * 1000;
  
  // Créer une sauvegarde immédiatement
  const backup = createBackup();
  localStorage.setItem('inventaire_auto_backup', JSON.stringify(backup));
  
  // Configurer la sauvegarde périodique
  const intervalId = setInterval(() => {
    try {
      const backup = createBackup();
      localStorage.setItem('inventaire_auto_backup', JSON.stringify(backup));
      console.log(`Sauvegarde automatique effectuée: ${new Date().toLocaleString()}`);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde automatique:', error);
    }
  }, interval);
  
  return intervalId;
};

/**
 * Arrête la sauvegarde automatique périodique
 * @param {number} intervalId - ID de l'intervalle à arrêter
 */
const stopAutoBackup = (intervalId) => {
  if (intervalId) {
    clearInterval(intervalId);
  }
};

/**
 * Restaure la dernière sauvegarde automatique
 * @returns {boolean} True si la restauration a réussi, false sinon
 */
const restoreAutoBackup = () => {
  try {
    const autoBackupString = localStorage.getItem('inventaire_auto_backup');
    if (!autoBackupString) {
      toast.error('Aucune sauvegarde automatique trouvée');
      return false;
    }
    
    const autoBackup = JSON.parse(autoBackupString);
    
    // Vérifier la structure de la sauvegarde
    if (!autoBackup.metadata || !autoBackup.data) {
      toast.error('Format de sauvegarde automatique invalide');
      return false;
    }
    
    // Afficher une confirmation avant de restaurer
    if (window.confirm(
      `Êtes-vous sûr de vouloir restaurer la sauvegarde automatique du ${new Date(autoBackup.metadata.timestamp).toLocaleString()}? 
      Cela remplacera toutes vos données actuelles.`
    )) {
      // Restaurer les données
      const { data } = autoBackup;
      
      // Restaurer chaque clé
      Object.keys(data).forEach(key => {
        localStorage.setItem(key, data[key]);
      });
      
      toast.success('Données restaurées avec succès depuis la sauvegarde automatique');
      return true;
    } else {
      toast.info('Restauration annulée');
      return false;
    }
  } catch (error) {
    console.error('Erreur lors de la restauration de la sauvegarde automatique:', error);
    toast.error('Erreur lors de la restauration de la sauvegarde automatique');
    return false;
  }
};

/**
 * Efface toutes les données de l'application
 * @returns {boolean} True si l'effacement a réussi, false sinon
 */
const clearAllData = () => {
  try {
    // Demander une confirmation
    if (window.confirm(
      'ATTENTION: Vous êtes sur le point de supprimer TOUTES les données de l\'application. Cette action est irréversible. Êtes-vous absolument sûr de vouloir continuer?'
    )) {
      // Créer une sauvegarde avant de tout effacer
      const backup = createBackup();
      localStorage.setItem('inventaire_pre_clear_backup', JSON.stringify(backup));
      
      // Effacer toutes les données
      BACKUP_KEYS.forEach(key => {
        localStorage.removeItem(key);
      });
      
      // Réinitialiser avec les données par défaut
      LocalStorageService.initializeDefaultData();
      
      toast.success('Toutes les données ont été effacées et réinitialisées');
      return true;
    } else {
      toast.info('Opération annulée');
      return false;
    }
  } catch (error) {
    console.error('Erreur lors de l\'effacement des données:', error);
    toast.error('Erreur lors de l\'effacement des données');
    return false;
  }
};

/**
 * Récupère les données après un effacement complet
 * @returns {boolean} True si la récupération a réussi, false sinon
 */
const recoverAfterClear = () => {
  try {
    const preClearBackupString = localStorage.getItem('inventaire_pre_clear_backup');
    if (!preClearBackupString) {
      toast.error('Aucune sauvegarde pré-effacement trouvée');
      return false;
    }
    
    const preClearBackup = JSON.parse(preClearBackupString);
    
    // Vérifier la structure de la sauvegarde
    if (!preClearBackup.metadata || !preClearBackup.data) {
      toast.error('Format de sauvegarde pré-effacement invalide');
      return false;
    }
    
    // Afficher une confirmation avant de restaurer
    if (window.confirm(
      `Êtes-vous sûr de vouloir récupérer les données d'avant l'effacement (${new Date(preClearBackup.metadata.timestamp).toLocaleString()})? 
      Cela remplacera toutes vos données actuelles.`
    )) {
      // Restaurer les données
      const { data } = preClearBackup;
      
      // Restaurer chaque clé
      Object.keys(data).forEach(key => {
        localStorage.setItem(key, data[key]);
      });
      
      toast.success('Données récupérées avec succès');
      return true;
    } else {
      toast.info('Récupération annulée');
      return false;
    }
  } catch (error) {
    console.error('Erreur lors de la récupération des données:', error);
    toast.error('Erreur lors de la récupération des données');
    return false;
  }
};

export const BackupService = {
  createBackup,
  exportBackup,
  importBackup,
  startAutoBackup,
  stopAutoBackup,
  restoreAutoBackup,
  clearAllData,
  recoverAfterClear
};